import type { EvaluationTargetPolicy } from "./evaluationTargetPolicy";

// 평가 대상 policy 를 학습 dataset 이름별로 묶는다. TASK 추천이 dataset 이름을 근거로 동작한다.
// 그래서 화면도 같은 축으로 묶는다.

export type EvalPolicyGroup = {
  // 그룹 헤더에 그대로 표시하는 문자열. registry 표시명, "training-infra 전용 데이터셋", "—" 중 하나다.
  label: string;
  policies: readonly EvaluationTargetPolicy[];
};

// locale 과 무관한 결정적 비교.
function compareLabelAsc(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

// 한 그룹은 같은 dataset 이므로 첫 policy 가 그룹 전체를 대표한다.
function hasDatasetName(group: EvalPolicyGroup): boolean {
  return group.policies[0].datasetName !== null;
}

// 그룹 키는 화면에 표시하는 `datasetLabel` 이다. `datasetName` 으로 묶으면 이름을 모르는 policy 가
// null 하나로 합쳐진다. 그러면 "training-infra 전용 데이터셋" 과 "—" 가 한 헤더 아래 섞인다.
export function groupPoliciesByDataset(
  policies: readonly EvaluationTargetPolicy[],
): readonly EvalPolicyGroup[] {
  const byLabel = new Map<string, EvaluationTargetPolicy[]>();
  for (const policy of policies) {
    const group = byLabel.get(policy.datasetLabel);
    if (group === undefined) {
      byLabel.set(policy.datasetLabel, [policy]);
      continue;
    }
    group.push(policy);
  }

  // 이름을 아는 그룹이 먼저다. 미상 그룹은 TASK 추천을 결정할 수 없다. 개수가 많다고 위에 오면
  // 사용자가 추천 근거를 잘못 읽는다. 그다음은 개수 내림차순, 마지막은 이름 오름차순이다.
  return [...byLabel]
    .map(([label, grouped]) => ({ label, policies: grouped }))
    .sort(
      (a, b) =>
        Number(hasDatasetName(b)) - Number(hasDatasetName(a)) ||
        b.policies.length - a.policies.length ||
        compareLabelAsc(a.label, b.label),
    );
}
